import React from 'react';
import moment, { duration } from 'moment';
import { DeepMap } from 'react-hook-form';
import {
  ChangeType,
  FileChangeInput,
  JobsiteOnboardingDocumentFileInput,
  JobsiteOnboardingDocumentInput,
} from 'apollo/generated/client-operations';
import { DocTypeKey, Editable, SingleInstanceDocTypeKey } from 'types';
import {
  cn,
  objectEntries,
  stringifyEmptyFields,
  tryFileCompression,
  typedComponentProps,
} from 'utils';
import { editableArrayRequiredValidation } from 'utils/validation';
import { FileVideoIcon } from 'components/icons';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { DocumentKey } from 'containers/worker/utils';
import {
  getAsDurationString,
  toggleBorderClasses,
} from 'containers/jobsiteConfiguration/helpers/utils';
import {
  DocumentFile,
  EditJobsiteConfigurationFormData,
  JobsiteSafetyVideo,
} from 'containers/jobsiteConfiguration/types';
import { DocumentFiles } from 'containers/jobsiteConfiguration/components';
import { Jobsite } from './types';

export type GetDocumentsSectionInputsArgs = {
  isJobsiteSafetyDocumentFilesRequired: boolean;
  isWorkerConsentDocumentFilesRequired: boolean;
};

export const documentsSectionInputs: TypedFormInputs<
  EditJobsiteConfigurationFormData['documents']
> = {
  osha: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      oshaEnabled: {
        element: FormInputTypes.Toggle,
        label: 'Osha Card',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      oshaRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  twic: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      twicEnabled: {
        element: FormInputTypes.Toggle,
        label: 'TWIC Card',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      twicRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  nycSst: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      nycSstEnabled: {
        element: FormInputTypes.Toggle,
        label: 'NYC SST',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      nycSstRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  governmentIdEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Government ID',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  siteSpecificOrientation: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      siteSpecificOrientationEnabled: {
        element: FormInputTypes.Toggle,
        label: 'Site-Specific Orientation',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      siteSpecificOrientationGracePeriodInterval: {
        element: FormInputTypes.Field,
        label: 'Grace Period Interval',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        elementProps: { fieldType: 'number', innerRightLabel: 'Hours' },
      },
      siteSpecificOrientationRefresherInterval: {
        element: FormInputTypes.Field,
        label: 'Refresher Interval',
        layout: [GridColSpan.SpanFull, GridColSpan.SmSpan3],
        elementProps: { fieldType: 'number', innerRightLabel: 'Months' },
      },
      siteSpecificOrientationRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  jobsiteSafetyVideos: {
    element: FormInputTypes.Repeater,
    elementProps: {
      shouldTrackChanges: { enabled: true, idField: 'key' },
      addItemText: 'Add Jobsite Safety Video',
      addItemIcon: FileVideoIcon,
      getNewItem: (newIndex: number): Editable<JobsiteSafetyVideo> => ({
        key: `${DocumentKey.JobsiteSafetyVideo}-${moment
          .utc()
          .format('YYYYMMDD-HHmmss-SSS')}`,
        name: '',
        docIndex: (newIndex + 1).toString(),
        jobsiteSafetyVideoEnabled: false,
        jobsiteSafetyVideoFiles: [],
        jobsiteSafetyVideoRequiredForOnboarding: false,
        changeType: null,
      }),
      suppressItemBorder: true,
      withSeparators: true,
      itemContainerClassName: cn('odin-py-5 -odin-mt-4'),
      itemClassName: cn('odin-grid odin-grid-cols-6 odin-gap-3'),
      isRemoveItemDisabled: true,
      removeItemPosition: 'top',
      removeItemClassName: '',
    },
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    children: {
      // RHF v6 (web-app) doesn't save `key` field without an input
      key: {
        element: FormInputTypes.Field,
        label: 'Key',
        layout: 'odin-hidden',
      },
      // RHF v6 (web-app) doesn't save `docIndex` field without an input
      docIndex: {
        element: FormInputTypes.Field,
        label: 'Doc Index',
        layout: 'odin-hidden',
      },
      // RHF v6 (web-app) doesn't save `changeType` field without an input
      // use CustomInput because the Field component doesn't support undefined as `defaultValue`
      changeType: {
        element: FormInputTypes.CustomInput,
        label: '',
        elementProps: { customInput: React.forwardRef(() => null) },
        layout: 'odin-hidden',
      },
      name: {
        element: FormInputTypes.Field,
        label: 'Jobsite Safety Video Name',
        layout: [GridColSpan.Span5],
        validation: { required: true },
      },
      jobsiteSafetyVideoEnabled: {
        element: FormInputTypes.Toggle,
        label: '',
        layout: [
          GridColSpan.Span1,
          cn('odin-mt-2 odin-flex odin-items-center odin-justify-end'),
        ],
        elementProps: { toggleAlignment: 'bottom' },
      },
      jobsiteSafetyVideoFiles: {
        element: FormInputTypes.CustomInput,
        elementProps: {
          customInput: DocumentFiles,
          ...typedComponentProps<typeof DocumentFiles>({
            accept: { 'video/*': [] },
          }),
        },
        validation: {
          validate: editableArrayRequiredValidation(
            'Jobsite Safety Video File',
            (
              _data: JobsiteSafetyVideo['jobsiteSafetyVideoFiles'],
              jsv: JobsiteSafetyVideo,
            ) => {
              return jsv.jobsiteSafetyVideoEnabled;
            },
          ),
        },
        layout: [GridColSpan.SpanFull],
      },
      jobsiteSafetyVideoRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  jobsiteSafetyDocument: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      jobsiteSafetyDocumentEnabled: {
        element: FormInputTypes.Toggle,
        label: 'Jobsite Safety Document Acknowledgment',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      jobsiteSafetyDocumentFiles: {
        element: FormInputTypes.CustomInput,
        elementProps: {
          customInput: DocumentFiles,
          ...typedComponentProps<typeof DocumentFiles>({
            accept: { 'text/html': ['.html', '.htm'] },
          }),
        },
        validation: {
          validate: editableArrayRequiredValidation(
            'Jobsite Safety Document File',
            (
              _data: EditJobsiteConfigurationFormData['documents']['jobsiteSafetyDocument']['jobsiteSafetyDocumentFiles'],
              formValues: EditJobsiteConfigurationFormData,
            ) => {
              return formValues.documents.jobsiteSafetyDocument
                .jobsiteSafetyDocumentEnabled;
            },
          ),
        },
        layout: [GridColSpan.SpanFull],
      },
      jobsiteSafetyDocumentRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  workerConsentDocument: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      workerConsentDocumentEnabled: {
        element: FormInputTypes.Toggle,
        label: 'Worker Consent Document',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      workerConsentDocumentFiles: {
        element: FormInputTypes.CustomInput,
        elementProps: {
          customInput: DocumentFiles,
          ...typedComponentProps<typeof DocumentFiles>({
            accept: { 'text/html': ['.html', '.htm'] },
          }),
        },
        validation: {
          validate: editableArrayRequiredValidation(
            'Worker Consent Document File',
            (
              _data: EditJobsiteConfigurationFormData['documents']['workerConsentDocument']['workerConsentDocumentFiles'],
              formValues: EditJobsiteConfigurationFormData,
            ) => {
              return formValues.documents.workerConsentDocument
                .workerConsentDocumentEnabled;
            },
          ),
        },
        layout: [GridColSpan.SpanFull],
      },
      workerConsentDocumentRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  additionalCertificationsEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Additional Certifications',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  genericEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Generic',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  medicalDrugTestingConsentFormEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Medical Drug Testing Consent Form',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  medicalCovid19OrientationAndProceduresEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Medical Covid19 Orientation And Procedures',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  medicalBreathAlcoholTestEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Medical Breath Alcohol Test',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  medicalUrineDrugTestEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Medical Urine Drug Test',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  postEmploymentBreathAlcoholTestEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Post Employment Breath Alcohol Test',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  postEmploymentUrineDrugTestEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Post Employment Urine Drug Test',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  signatureEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Signature',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  hudsonYardsEmployeeOrientationEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Hudson Yards Employee Orientation',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  monadnockEyeProtectionAcknowledgementEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Monadnock Eye Protection Acknowledgement',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
  lirrBlueTraining: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      lirrBlueTrainingEnabled: {
        element: FormInputTypes.Toggle,
        label: 'LIRR Blue Training',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      lirrBlueTrainingRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  mustSafetyReport: {
    element: FormInputTypes.Panel,
    layout: [GridColSpan.SpanFull],
    children: {
      mustSafetyReportEnabled: {
        element: FormInputTypes.Toggle,
        label: 'MUST Safety Report',
        layout: [GridColSpan.SpanFull],
        elementProps: { toggleAlignment: 'right-with-space' },
      },
      mustSafetyReportRequiredForOnboarding: {
        element: FormInputTypes.Toggle,
        label: 'Enforced',
        layout: [GridColSpan.SpanFull],
      },
    },
  },
  confinedSpacesTrainingEnabled: {
    element: FormInputTypes.Toggle,
    label: 'Confined Spaces Training',
    layout: [GridColSpan.SpanFull, toggleBorderClasses],
    elementProps: { toggleAlignment: 'right-with-space' },
  },
};

export const getDocumentsSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['documents'] => {
  const documentTypes = jobsite?.documentTypes.edges.map(({ node }) => node);

  const { documents } = jobsite?.onboardingModule ?? {};

  const getDocumentFiles = (docKey: DocTypeKey): Editable<DocumentFile>[] => {
    const documentType = documentTypes?.find(
      (dt) => (dt.key ?? dt.workerDocumentType.key) === docKey,
    );
    return (
      documentType?.files?.map((file) => ({
        id: file.fileId,
        language: file.language,
        title: file.title,
        file,
      })) ?? []
    );
  };

  const isDocumentAssigned = (docTypeKey: DocTypeKey): boolean => {
    return (
      documentTypes?.some(
        (dt) => (dt.key ?? dt.workerDocumentType.key) === docTypeKey,
      ) ?? false
    );
  };

  const isDocumentRequired = (docTypeKey: DocTypeKey): boolean => {
    return documents?.find((d) => d.key === docTypeKey)?.isRequired ?? false;
  };

  const isDocumentVisible = (docTypeKey: DocTypeKey): boolean => {
    return documents?.find((d) => d.key === docTypeKey)?.isVisible ?? false;
  };

  const isDocumentEnabled = (docTypeKey: DocTypeKey): boolean => {
    return isDocumentVisible(docTypeKey) && isDocumentAssigned(docTypeKey);
  };

  const getDocumentGracePeriod = (docKey: SingleInstanceDocTypeKey): string => {
    const gracePeriod =
      documents?.find((d) => d.key === docKey)?.gracePeriod ?? null;
    return gracePeriod && duration(gracePeriod).asHours().toString();
  };

  const getDocumentExpiration = (docKey: SingleInstanceDocTypeKey): string => {
    const expiration =
      documents?.find((d) => d.key === docKey)?.expiration ?? null;
    return expiration && duration(expiration).asMonths().toString();
  };

  return {
    osha: {
      oshaEnabled: isDocumentEnabled(DocumentKey.OshaCard),
      oshaRequiredForOnboarding: isDocumentRequired(DocumentKey.OshaCard),
    },
    twic: {
      twicEnabled: isDocumentEnabled(DocumentKey.TwicCard),
      twicRequiredForOnboarding: isDocumentRequired(DocumentKey.TwicCard),
    },
    nycSst: {
      nycSstEnabled: isDocumentEnabled(DocumentKey.NycSiteSafetyTrainingCard),
      nycSstRequiredForOnboarding: isDocumentRequired(
        DocumentKey.NycSiteSafetyTrainingCard,
      ),
    },
    governmentIdEnabled: isDocumentEnabled(DocumentKey.GovernmentIssuedId),
    siteSpecificOrientation: {
      siteSpecificOrientationEnabled: isDocumentEnabled(
        DocumentKey.SiteSpecificOrientation,
      ),
      ...stringifyEmptyFields({
        siteSpecificOrientationGracePeriodInterval: getDocumentGracePeriod(
          DocumentKey.SiteSpecificOrientation,
        ),
        siteSpecificOrientationRefresherInterval: getDocumentExpiration(
          DocumentKey.SiteSpecificOrientation,
        ),
      }),
      siteSpecificOrientationRequiredForOnboarding: isDocumentRequired(
        DocumentKey.SiteSpecificOrientation,
      ),
    },
    // `documents.jobsiteSafetyVideos` is bound to FormRepeater,
    // which leads to getValues() excluding it from the output
    // when it's an empty array.
    // In order to get the expected behavior when changes are reverted,
    // we need to pass `undefined` instead of an empty array as default value.
    ...((): Pick<
      EditJobsiteConfigurationFormData['documents'],
      'jobsiteSafetyVideos'
    > => {
      const jobsiteSafetyVideos = documentTypes
        ?.filter(
          (dt) => dt.workerDocumentType.key === DocumentKey.JobsiteSafetyVideo,
        )
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((dt): Editable<JobsiteSafetyVideo> => {
          const dtKey = dt.key as DocTypeKey;
          return {
            key: dtKey,
            changeType: null,
            name: dt.name,
            docIndex: dt.position?.toString() ?? '',
            jobsiteSafetyVideoEnabled: isDocumentEnabled(dtKey),
            jobsiteSafetyVideoRequiredForOnboarding: isDocumentRequired(dtKey),
            jobsiteSafetyVideoFiles: getDocumentFiles(dtKey),
          };
        });
      return jobsiteSafetyVideos?.length ? { jobsiteSafetyVideos } : undefined;
    })(),
    jobsiteSafetyDocument: {
      jobsiteSafetyDocumentEnabled: isDocumentEnabled(
        DocumentKey.JobsiteSafetyDocument,
      ),
      jobsiteSafetyDocumentRequiredForOnboarding: isDocumentRequired(
        DocumentKey.JobsiteSafetyDocument,
      ),
      jobsiteSafetyDocumentFiles: getDocumentFiles(
        DocumentKey.JobsiteSafetyDocument,
      ),
    },
    workerConsentDocument: {
      workerConsentDocumentEnabled: isDocumentEnabled(
        DocumentKey.WorkerConsentDocument,
      ),
      workerConsentDocumentRequiredForOnboarding: isDocumentRequired(
        DocumentKey.WorkerConsentDocument,
      ),
      workerConsentDocumentFiles: getDocumentFiles(
        DocumentKey.WorkerConsentDocument,
      ),
    },

    additionalCertificationsEnabled: isDocumentEnabled(
      DocumentKey.AdditionalCertifications,
    ),
    genericEnabled: isDocumentEnabled(DocumentKey.Generic),

    medicalDrugTestingConsentFormEnabled: isDocumentEnabled(
      DocumentKey.MedicalDrugTestingConsentForm,
    ),
    medicalCovid19OrientationAndProceduresEnabled: isDocumentEnabled(
      DocumentKey.MedicalCovid19OrientationAndProcedures,
    ),
    medicalBreathAlcoholTestEnabled: isDocumentEnabled(
      DocumentKey.MedicalBreathAlcoholTest,
    ),
    medicalUrineDrugTestEnabled: isDocumentEnabled(
      DocumentKey.MedicalUrineDrugTest,
    ),
    postEmploymentBreathAlcoholTestEnabled: isDocumentEnabled(
      DocumentKey.PostEmploymentBreathAlcoholTest,
    ),
    postEmploymentUrineDrugTestEnabled: isDocumentEnabled(
      DocumentKey.PostEmploymentUrineDrugTest,
    ),

    signatureEnabled: isDocumentEnabled(DocumentKey.Signature),
    hudsonYardsEmployeeOrientationEnabled: isDocumentEnabled(
      DocumentKey.HudsonYardsEmployeeOrientation,
    ),
    monadnockEyeProtectionAcknowledgementEnabled: isDocumentEnabled(
      DocumentKey.MonadnockEyeProtectionAcknowledgement,
    ),

    lirrBlueTraining: {
      lirrBlueTrainingEnabled: isDocumentEnabled(DocumentKey.LIRRBlueTraining),
      lirrBlueTrainingRequiredForOnboarding: isDocumentRequired(
        DocumentKey.LIRRBlueTraining,
      ),
    },
    mustSafetyReport: {
      mustSafetyReportEnabled: isDocumentEnabled(DocumentKey.MustSafetyReport),
      mustSafetyReportRequiredForOnboarding: isDocumentRequired(
        DocumentKey.MustSafetyReport,
      ),
    },
    confinedSpacesTrainingEnabled: isDocumentEnabled(
      DocumentKey.ConfinedSpacesTraining,
    ),
  };
};

export type OnboardingModuleDocumentInputs = {
  documents: JobsiteOnboardingDocumentInput[];
};

export const getOnboardingModuleDocumentInputs = (
  documents: EditJobsiteConfigurationFormData['documents'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['documents'], true>,
): OnboardingModuleDocumentInputs => {
  const {
    siteSpecificOrientation,
    jobsiteSafetyVideos,
    jobsiteSafetyDocument,
    workerConsentDocument,
    nycSst,
    osha,
    twic,
    lirrBlueTraining,
    mustSafetyReport,
  } = documents;

  const getUpdateInputValue = getUpdateInputValueFunction(
    documents,
    dirtyFields,
  );
  const getOshaUpdateInputValue = getUpdateInputValueFunction(
    osha,
    dirtyFields?.osha,
  );
  const getTwicUpdateInputValue = getUpdateInputValueFunction(
    twic,
    dirtyFields?.twic,
  );
  const getNycSstUpdateInputValue = getUpdateInputValueFunction(
    nycSst,
    dirtyFields?.nycSst,
  );
  const getSsoUpdateInputValue = getUpdateInputValueFunction(
    siteSpecificOrientation,
    dirtyFields?.siteSpecificOrientation,
  );
  const getJobsiteSafetyDocumentUpdateInputValue = getUpdateInputValueFunction(
    jobsiteSafetyDocument,
    dirtyFields?.jobsiteSafetyDocument,
  );
  const getWorkerConsentDocumentUpdateInputValue = getUpdateInputValueFunction(
    workerConsentDocument,
    dirtyFields?.workerConsentDocument,
  );
  const getLirrUpdateInputValue = getUpdateInputValueFunction(
    lirrBlueTraining,
    dirtyFields?.lirrBlueTraining,
  );
  const getMustSafetyReportUpdateInputValue = getUpdateInputValueFunction(
    mustSafetyReport,
    dirtyFields?.mustSafetyReport,
  );

  const byNonUndefinedFields = (
    documentInput: JobsiteOnboardingDocumentInput,
  ): boolean => {
    return objectEntries(documentInput).some(
      ([field, value]) =>
        field !== 'key' && field !== 'workerDocTypeKey' && value !== undefined,
    );
  };

  const documentsInputs: JobsiteOnboardingDocumentInput[] = [
    {
      key: DocumentKey.OshaCard,
      isVisible: getOshaUpdateInputValue('oshaEnabled'),
      isRequired: getOshaUpdateInputValue('oshaRequiredForOnboarding'),
    },
    {
      key: DocumentKey.TwicCard,
      isVisible: getTwicUpdateInputValue('twicEnabled'),
      isRequired: getTwicUpdateInputValue('twicRequiredForOnboarding'),
    },
    {
      key: DocumentKey.NycSiteSafetyTrainingCard,
      isVisible: getNycSstUpdateInputValue('nycSstEnabled'),
      isRequired: getNycSstUpdateInputValue('nycSstRequiredForOnboarding'),
    },
    {
      key: DocumentKey.GovernmentIssuedId,
      isVisible: getUpdateInputValue('governmentIdEnabled'),
    },
    {
      key: DocumentKey.SiteSpecificOrientation,
      isVisible: getSsoUpdateInputValue('siteSpecificOrientationEnabled'),
      isRequired: getSsoUpdateInputValue(
        'siteSpecificOrientationRequiredForOnboarding',
      ),
      gracePeriod: getAsDurationString(
        getSsoUpdateInputValue('siteSpecificOrientationGracePeriodInterval'),
        'hours',
      ),
      expiration: getAsDurationString(
        getSsoUpdateInputValue('siteSpecificOrientationRefresherInterval'),
        'months',
      ),
    },
    ...(jobsiteSafetyVideos
      ?.map(
        (jobsiteSafetyVideo, index): JobsiteOnboardingDocumentInput | null => {
          // Skip documents that have not changed
          if (!jobsiteSafetyVideo.changeType) return null;
          // We don't filter out documents without changeType before this `map`
          // to keep the correct `index` for dirtyFields access,
          // That's because `dirtyFields?.jobsiteSafetyVideos` is a `sparse array`,
          // where some elements are missing, only non-undefined elements being present.

          const jobsiteSafetyVideoDirtyField =
            dirtyFields?.jobsiteSafetyVideos?.[index];

          const getJobsiteSafetyVideoUpdateInputValue =
            getUpdateInputValueFunction(
              jobsiteSafetyVideo,
              jobsiteSafetyVideoDirtyField,
            );

          const docIndexInputValue =
            getJobsiteSafetyVideoUpdateInputValue('docIndex');

          return {
            key: jobsiteSafetyVideo.key,
            workerDocTypeKey: DocumentKey.JobsiteSafetyVideo,
            changeType: jobsiteSafetyVideo.changeType,
            name: getJobsiteSafetyVideoUpdateInputValue('name'),
            index: docIndexInputValue ? Number(docIndexInputValue) : undefined,
            isVisible:
              getJobsiteSafetyVideoUpdateInputValue(
                'jobsiteSafetyVideoEnabled',
              ) ??
              // force document to be updated if file only has changed
              (jobsiteSafetyVideoDirtyField?.jobsiteSafetyVideoFiles
                ? jobsiteSafetyVideo.jobsiteSafetyVideoEnabled ?? false
                : undefined),
            isRequired: getJobsiteSafetyVideoUpdateInputValue(
              'jobsiteSafetyVideoRequiredForOnboarding',
            ),
          };
        },
      )
      .filter(Boolean) ?? []),
    {
      key: DocumentKey.JobsiteSafetyDocument,
      isVisible:
        getJobsiteSafetyDocumentUpdateInputValue(
          'jobsiteSafetyDocumentEnabled',
        ) ??
        // force document to be updated if file only has changed
        (dirtyFields?.jobsiteSafetyDocument?.jobsiteSafetyDocumentFiles
          ? jobsiteSafetyDocument.jobsiteSafetyDocumentEnabled ?? false
          : undefined),
      isRequired: getJobsiteSafetyDocumentUpdateInputValue(
        'jobsiteSafetyDocumentRequiredForOnboarding',
      ),
    },
    {
      key: DocumentKey.WorkerConsentDocument,
      isVisible:
        getWorkerConsentDocumentUpdateInputValue(
          'workerConsentDocumentEnabled',
        ) ??
        // force document to be updated if file only has changed
        (dirtyFields?.workerConsentDocument?.workerConsentDocumentFiles
          ? workerConsentDocument.workerConsentDocumentEnabled ?? false
          : undefined),
      isRequired: getWorkerConsentDocumentUpdateInputValue(
        'workerConsentDocumentRequiredForOnboarding',
      ),
    },

    {
      key: DocumentKey.AdditionalCertifications,
      isVisible: getUpdateInputValue('additionalCertificationsEnabled'),
    },
    {
      key: DocumentKey.Generic,
      isVisible: getUpdateInputValue('genericEnabled'),
    },
    {
      key: DocumentKey.MedicalDrugTestingConsentForm,
      isVisible: getUpdateInputValue('medicalDrugTestingConsentFormEnabled'),
    },
    {
      key: DocumentKey.MedicalCovid19OrientationAndProcedures,
      isVisible: getUpdateInputValue(
        'medicalCovid19OrientationAndProceduresEnabled',
      ),
    },
    {
      key: DocumentKey.MedicalBreathAlcoholTest,
      isVisible: getUpdateInputValue('medicalBreathAlcoholTestEnabled'),
    },
    {
      key: DocumentKey.MedicalUrineDrugTest,
      isVisible: getUpdateInputValue('medicalUrineDrugTestEnabled'),
    },
    {
      key: DocumentKey.PostEmploymentBreathAlcoholTest,
      isVisible: getUpdateInputValue('postEmploymentBreathAlcoholTestEnabled'),
    },
    {
      key: DocumentKey.PostEmploymentUrineDrugTest,
      isVisible: getUpdateInputValue('postEmploymentUrineDrugTestEnabled'),
    },
    {
      key: DocumentKey.Signature,
      isVisible: getUpdateInputValue('signatureEnabled'),
    },
    {
      key: DocumentKey.HudsonYardsEmployeeOrientation,
      isVisible: getUpdateInputValue('hudsonYardsEmployeeOrientationEnabled'),
    },
    {
      key: DocumentKey.MonadnockEyeProtectionAcknowledgement,
      isVisible: getUpdateInputValue(
        'monadnockEyeProtectionAcknowledgementEnabled',
      ),
    },
    {
      key: DocumentKey.LIRRBlueTraining,
      isVisible: getLirrUpdateInputValue('lirrBlueTrainingEnabled'),
      isRequired: getLirrUpdateInputValue(
        'lirrBlueTrainingRequiredForOnboarding',
      ),
    },
    {
      key: DocumentKey.MustSafetyReport,
      isVisible: getMustSafetyReportUpdateInputValue('mustSafetyReportEnabled'),
      isRequired: getMustSafetyReportUpdateInputValue(
        'mustSafetyReportRequiredForOnboarding',
      ),
    },
    {
      key: DocumentKey.ConfinedSpacesTraining,
      isVisible: getUpdateInputValue('confinedSpacesTrainingEnabled'),
    },
  ];

  return {
    documents: documentsInputs.filter(byNonUndefinedFields),
  };
};

export const getOnboardingDocumentFilesInputs = async (
  jobsite: Jobsite,
  documents: EditJobsiteConfigurationFormData['documents'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['documents'], true>,
): Promise<JobsiteOnboardingDocumentFileInput[]> => {
  const { jobsiteSafetyVideos, jobsiteSafetyDocument, workerConsentDocument } =
    documents;
  const getJobsiteSafetyDocumentUpdateInputValue = getUpdateInputValueFunction(
    jobsiteSafetyDocument,
    dirtyFields?.jobsiteSafetyDocument,
  );
  const getWorkerConsentDocumentUpdateInputValue = getUpdateInputValueFunction(
    workerConsentDocument,
    dirtyFields?.workerConsentDocument,
  );

  const byNonUndefinedFields = (
    documentFileInput: JobsiteOnboardingDocumentFileInput,
  ): boolean => {
    return objectEntries(documentFileInput).some(
      ([field, value]) => field !== 'docTypeKey' && value !== undefined,
    );
  };

  const getFilesInput = async (
    files: Editable<DocumentFile>[],
  ): Promise<FileChangeInput[]> => {
    return (
      files &&
      Promise.all(
        files
          .filter((df) => df.changeType)
          .map(async (df): Promise<FileChangeInput> => {
            const fileData =
              df.changeType !== ChangeType.Removed && df.file instanceof Blob
                ? await tryFileCompression(df.file as File)
                : undefined;
            return {
              changeType: df.changeType,
              fileId: df.changeType !== ChangeType.Created ? df.id : undefined,
              fileInput:
                df.changeType !== ChangeType.Removed
                  ? {
                      uploadData: fileData,
                      isPublic: false,
                      language: df.language,
                      title: df.title,
                    }
                  : undefined,
            };
          }),
      )
    );
  };

  const documentsInputs: JobsiteOnboardingDocumentFileInput[] = [
    ...(
      await Promise.all(
        jobsiteSafetyVideos?.map(
          async (
            jobsiteSafetyVideo,
            index,
          ): Promise<JobsiteOnboardingDocumentFileInput> => {
            // Skip documents that have not changed
            if (!jobsiteSafetyVideo.changeType) return null;
            // We don't filter out documents without changeType before this `map`
            // to keep the correct `index` for dirtyFields access,
            // That's because `dirtyFields?.jobsiteSafetyVideos` is a `sparse array`,
            // where some elements are missing, only non-undefined elements being present.

            const getJobsiteSafetyVideoUpdateInputValue =
              getUpdateInputValueFunction(
                jobsiteSafetyVideo,
                dirtyFields?.jobsiteSafetyVideos?.[index],
              );

            const jobsiteSafetyVideoFilesInput =
              getJobsiteSafetyVideoUpdateInputValue('jobsiteSafetyVideoFiles');

            return {
              docTypeKey: jobsiteSafetyVideo.key,
              files: jobsiteSafetyVideoFilesInput?.some((f) => f.changeType)
                ? await getFilesInput(jobsiteSafetyVideoFilesInput)
                : undefined,
            };
          },
        ) ?? [],
      )
    ).filter(Boolean),
    {
      docTypeKey: DocumentKey.JobsiteSafetyDocument,
      files: await getFilesInput(
        getJobsiteSafetyDocumentUpdateInputValue('jobsiteSafetyDocumentFiles'),
      ),
    },
    {
      docTypeKey: DocumentKey.WorkerConsentDocument,
      files: await getFilesInput(
        getWorkerConsentDocumentUpdateInputValue('workerConsentDocumentFiles'),
      ),
    },
  ];

  return documentsInputs.filter(byNonUndefinedFields);
};
